import { EventInput } from '@fullcalendar/core';
import {
  computeDurationMinutes,
  formatDurationLabel,
  hasValidMachineryId,
  isSameDay,
} from '../calendar.utils';
import {
  CALENDAR_EVENT_STATE,
  CalendarEvent,
  CustomEventStyleOptions,
} from '../models/calendar.model';

/** Fixed synthetic span for a promemoria's timed-grid box (see `PROMEMORIA_GRID_DURATION_MS` usage). */
const PROMEMORIA_GRID_DURATION_MS = 30 * 60 * 1000;

/**
 * Kind of a calendar event, resolved from its tipoEvento.
 */
type TipoEventoCalendario =
  | 'turno'
  | 'assenza'
  | 'riunione'
  | 'promemoria'
  | 'travel'
  | 'custom'
  | 'appuntamento';

/**
 * Kinds of a calendar event that are not an appuntamento.
 */
type TipoEventoOperatore = Exclude<TipoEventoCalendario, 'appuntamento'>;

/**
 * Colors of a calendar event.
 */
type EventColors = Required<CustomEventStyleOptions>;

/**
 * Appuntamento state that drives its colors and classes.
 */
interface AppuntamentoState {
  isMacchinarioSlot: boolean;
  hasMatchingPrestazione: boolean;
  isErogato: boolean;
  notSvolto: boolean;
}

/** Colors of a riunione, shared by the custom tipi. */
const RIUNIONE_COLORS: EventColors = {
  backgroundColor: '#FDF4FF',
  borderColor: '#D946EF',
  textColor: '#86198F',
};

/** Colors of each non-appuntamento kind. */
const OPERATORE_COLORS: Record<TipoEventoOperatore, EventColors> = {
  turno: {
    backgroundColor: '#F0FDF4',
    borderColor: '#16A34A',
    textColor: '#14532D',
  },
  assenza: {
    backgroundColor: '#F3F4F6',
    borderColor: '#4B5563',
    textColor: '#374151',
  },
  riunione: RIUNIONE_COLORS,
  promemoria: {
    backgroundColor: '#FFF7ED',
    borderColor: '#EA580C',
    textColor: '#9A3412',
  },
  travel: {
    backgroundColor: '#F1F5F9',
    borderColor: '#94A3B8',
    textColor: '#475569',
  },
  custom: RIUNIONE_COLORS,
};

/** Colors of an appuntamento of another prestazione. */
const APPUNTAMENTO_COLORS: EventColors = {
  backgroundColor: '#F0F9FF',
  borderColor: '#0284C7',
  textColor: '#0C4A6E',
};

/** Colors of a macchinario slot. */
const MACCHINARIO_COLORS: EventColors = {
  backgroundColor: '#EDE7F6',
  borderColor: '#6A1B9A',
  textColor: '#4A148C',
};

/** Colors of an appuntamento of the current prestazione. */
const PRESTAZIONE_CORRENTE_COLORS: EventColors = {
  backgroundColor: '#294A63',
  borderColor: '#294A63',
  textColor: '#FFFFFF',
};

/** Colors of an erogato appuntamento of the current prestazione. */
const PRESTAZIONE_CORRENTE_EROGATA_COLORS: EventColors = {
  ...PRESTAZIONE_CORRENTE_COLORS,
  textColor: '#0C4A6E',
};

/** CSS class of each non-appuntamento kind; the custom tipi share the riunione's. */
const OPERATORE_CLASS: Record<TipoEventoOperatore, string> = {
  turno: 'turno-event',
  assenza: 'assenza-event',
  riunione: 'riunione-event',
  promemoria: 'promemoria-event',
  travel: 'travel-event',
  custom: 'riunione-event',
};

/** Kinds rendered as a background event and never editable. */
const BACKGROUND_TIPI: TipoEventoCalendario[] = ['turno', 'assenza', 'travel'];

/**
 * Forces the end of an allDay event to be exclusive (see `toAllDayExclusiveEnd` usage).
 */
function toAllDayExclusiveEnd(end: Date): Date {
  const exclusiveEnd = new Date(end);
  const hasTimeOfDay =
    exclusiveEnd.getHours() !== 0 ||
    exclusiveEnd.getMinutes() !== 0 ||
    exclusiveEnd.getSeconds() !== 0;
  exclusiveEnd.setHours(0, 0, 0, 0);
  if (hasTimeOfDay) exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
  return exclusiveEnd;
}

/**
 * Checks if the date range spans a full day.
 */
function isFullDayRange(start: Date, end: Date): boolean {
  const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
  const endsAtDayBoundary =
    (end.getHours() === 0 && end.getMinutes() === 0) ||
    (end.getHours() === 23 && end.getMinutes() === 59);
  return startsAtMidnight && endsAtDayBoundary;
}

export const isDateValidForEvent = (date: Date): boolean => {
  return date >= new Date(Date.now() - 60000);
};

export const isDateAssignableToMe = (
  dateStart: Date,
  dateEnd: Date,
): boolean => {
  const tempStart = new Date(dateStart);
  tempStart.setMinutes(tempStart.getMinutes() + 1);
  return tempStart <= new Date(Date.now()) && dateEnd >= new Date(Date.now());
};

/**
 * True if the event has passed its erogation deadline.
 */
export const isErogato = (event: CalendarEvent): boolean => {
  const erogazionePassata =
    !!event.dataFineErogazione &&
    !isDateValidForEvent(new Date(event.dataFineErogazione));
  const appuntamentoPassato =
    !!event.dataFineEvento &&
    !isDateValidForEvent(new Date(event.dataFineEvento));
  return erogazionePassata || appuntamentoPassato;
};

/**
 * True if the event (or its erogation) has already started, or is marked "non svolto" — used to unlock "Avanti".
 */
export const isAvviato = (event: CalendarEvent): boolean => {
  const nonSvolto =
    event.stato === CALENDAR_EVENT_STATE.NON_SVOLTO ||
    event.stato === CALENDAR_EVENT_STATE.NON_SVOLTO_E_INVIATO_A_CASSA;
  const erogazioneIniziata =
    !!event.dataInizioErogazione &&
    !isDateValidForEvent(new Date(event.dataInizioErogazione));
  const appuntamentoIniziato =
    !!event.dataInizioEvento &&
    !isDateValidForEvent(new Date(event.dataInizioEvento));
  return nonSvolto || erogazioneIniziata || appuntamentoIniziato;
};

/**
 * Maps an array of CalendarEvent objects to an array of EventInput objects for FullCalendar.
 * @returns An array of EventInput objects.
 */
export const mapApiToCalendarEvents = (
  apiEvents: CalendarEvent[],
  idPrestazione?: string,
): EventInput[] => {
  if (!apiEvents) return [];
  const now = new Date();

  return apiEvents.flatMap((event) =>
    mapApiToCalendarEvent(event, idPrestazione, now),
  );
};

/**
 * Maps a CalendarEvent to its FullCalendar events: none, the event alone, or the event plus its allDay copy.
 * @returns {EventInput[]} The FullCalendar events
 */
function mapApiToCalendarEvent(
  event: CalendarEvent,
  idPrestazione: string | undefined,
  now: Date,
): EventInput[] {
  const startDate = event.dataInizioEvento;
  const endDate = event.dataFineEvento;
  const startDateObj = new Date(startDate as string);
  const endDateObj = new Date(endDate as string);
  const tipo = resolveTipoEvento(event);
  const travelDurationMinutes =
    tipo === 'travel' ? computeDurationMinutes(startDateObj, endDateObj) : null;
  if (tipo === 'travel' && travelDurationMinutes === 0) return [];

  const state: AppuntamentoState = {
    isMacchinarioSlot: hasValidMachineryId(event.idMacchinario),
    hasMatchingPrestazione: event?.idPrestazione === idPrestazione,
    isErogato: !!event?.dataInizioErogazione,
    notSvolto: event?.stato !== CALENDAR_EVENT_STATE.NON_SVOLTO,
  };
  const colors = resolveColors(tipo, state);
  const isBackground = BACKGROUND_TIPI.includes(tipo);
  const isPromemoria = tipo === 'promemoria';

  const calendarEvent: EventInput = {
    id: event.id,
    title: buildEventTitle(event, tipo, travelDurationMinutes),
    start: startDate,
    end: isPromemoria
      ? new Date(startDateObj.getTime() + PROMEMORIA_GRID_DURATION_MS)
      : endDate,
    backgroundColor: event.backgroundColor || colors.backgroundColor,
    borderColor: event.borderColor || colors.borderColor,
    textColor: event.textColor || colors.textColor,
    classNames: buildClassNames(tipo, state),
    display: isBackground ? 'background' : 'block',
    editable:
      !isBackground && state.hasMatchingPrestazione && startDateObj > now,
    durationEditable: false,
    extendedProps: {
      ...event,
      hasMatchingPrestazione: state.hasMatchingPrestazione,
      notSvolto: state.notSvolto,
      ...(isPromemoria ? { realStart: startDate, realEnd: endDate } : null),
    },
  };

  if (!shouldShowAllDaySummary(tipo, event)) {
    return [calendarEvent];
  }
  return [calendarEvent, buildAllDayCopy(calendarEvent, event, tipo)];
}

/**
 * Resolves the kind of a calendar event from its tipoEvento id and name.
 * @returns {TipoEventoCalendario} The event kind, `custom` for the tipi added from the typology
 */
function resolveTipoEvento(event: CalendarEvent): TipoEventoCalendario {
  const tipoEvento = event?.tipoEvento;
  const nome = tipoEvento?.nome?.toLowerCase() ?? '';
  if (tipoEvento?.id === 4 || nome === 'turno') return 'turno';
  if (tipoEvento?.id === 3 || nome === 'assenza') return 'assenza';
  if (nome === 'riunione') return 'riunione';
  if (nome === 'promemoria') return 'promemoria';
  if (nome === 'travel time') return 'travel';
  if (
    !tipoEvento ||
    tipoEvento.id === 1 ||
    tipoEvento.id === 2 ||
    nome === 'appuntamento'
  ) {
    return 'appuntamento';
  }
  return 'custom';
}

/**
 * Resolves the default colors of an event, before the ones sent by the backend.
 * @returns {EventColors} The event colors
 */
function resolveColors(
  tipo: TipoEventoCalendario,
  state: AppuntamentoState,
): EventColors {
  if (tipo !== 'appuntamento') return OPERATORE_COLORS[tipo];
  if (state.isMacchinarioSlot) return MACCHINARIO_COLORS;
  if (!state.hasMatchingPrestazione) return APPUNTAMENTO_COLORS;
  return state.isErogato
    ? PRESTAZIONE_CORRENTE_EROGATA_COLORS
    : PRESTAZIONE_CORRENTE_COLORS;
}

/**
 * Builds the title shown on the event card.
 * @returns {string} The event title
 */
function buildEventTitle(
  event: CalendarEvent,
  tipo: TipoEventoCalendario,
  travelDurationMinutes: number | null,
): string {
  switch (tipo) {
    case 'turno':
      return event.titolo || 'Turno';
    case 'assenza':
      return event.titolo || 'Assenza';
    case 'riunione':
    case 'promemoria':
    case 'custom':
      return event.titolo || event?.tipoEvento?.nome || 'Evento';
    case 'travel':
      return `Tragitto (${formatDurationLabel(travelDurationMinutes)})${event.nomePaziente ? ' - ' + event.nomePaziente : ''}`;
    default:
      return event?.nomePrestazione || event.nomePaziente;
  }
}

/**
 * Builds the CSS classes of an event.
 * @returns {string[]} The event classes
 */
function buildClassNames(
  tipo: TipoEventoCalendario,
  state: AppuntamentoState,
): string[] {
  if (tipo !== 'appuntamento') return ['pl-2', OPERATORE_CLASS[tipo]];
  const base = ['pl-2', 'appuntamento-event'];
  if (state.isMacchinarioSlot) return [...base, 'macchinario-event'];
  if (state.hasMatchingPrestazione) {
    return [
      ...base,
      state.isErogato ? 'appuntamento-erogato' : 'prestazione-corrente-event',
    ];
  }
  if (!state.isErogato) return base;
  return state.notSvolto
    ? [...base, 'appuntamento-erogato', 'appuntamento-nonsvolto']
    : [...base, 'appuntamento-erogato'];
}

/**
 * Tells whether the event also needs an allDay copy in the calendar header.
 * @returns {boolean} True for full-day or multi-day assenze, promemoria and other multi-day events
 */
function shouldShowAllDaySummary(
  tipo: TipoEventoCalendario,
  event: CalendarEvent,
): boolean {
  const start = new Date(event.dataInizioEvento as string);
  const end = new Date(event.dataFineEvento as string);
  const hasValidDates =
    !!event.dataInizioEvento &&
    !!event.dataFineEvento &&
    !isNaN(start.getTime()) &&
    !isNaN(end.getTime());
  const isMultiDay = hasValidDates && !isSameDay(start, end);
  if (tipo === 'assenza') {
    return hasValidDates && (isFullDayRange(start, end) || isMultiDay);
  }
  return tipo !== 'travel' && (tipo === 'promemoria' || isMultiDay);
}

/**
 * Builds the allDay copy of an event, keeping its real dates in the extendedProps.
 * @returns {EventInput} The allDay copy
 */
function buildAllDayCopy(
  calendarEvent: EventInput,
  event: CalendarEvent,
  tipo: TipoEventoCalendario,
): EventInput {
  return {
    ...calendarEvent,
    end: toAllDayExclusiveEnd(new Date(event.dataFineEvento as string)),
    allDay: true,
    display:
      tipo === 'turno' || tipo === 'assenza' ? 'auto' : calendarEvent.display,
    extendedProps: {
      ...calendarEvent.extendedProps,
      realStart: event.dataInizioEvento,
      realEnd: event.dataFineEvento,
    },
  };
}
